import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { DT, DS2 } from '../theme/designTokens';
import { useIsMobile } from '../hooks/useBreakpoint';
import { PageContent } from '../components/ui/PageContent';
import { AtmosphericGlow } from '../components/ui/AtmosphericGlow';
import { FilterPill } from '../components/ui/FilterPill';
import { Footer } from '../components/shell/Footer';
import { apiClient } from '../network/ApiClient';
import { useFriends } from '../context/FriendContext';
import { showSocialToast } from '../components/social/SocialToastManager';
import { uiFromError, renderUiMessage } from '../i18n/uiMessage';
import type { UserSearchResult, SearchFriendshipStatus } from '../types/social';
import type { PartnerStats } from '../types/game';
import { CountryFlag } from '../components/ui/CountryFlag';
import { PlayerAvatar } from '../components/ui/PlayerAvatar';
import { UnreadBadge } from '../components/ui/UnreadBadge';
import { useTranslation } from '../i18n/react';

/**
 * FRIENDS — friends list + played-with panel (Figma "FRIENDS" frame,
 * 2560×2760 export, values ×0.75, DS2 fluid tokens for shared styles).
 *
 * BACKEND SLOTS (deliberately empty until wired — see FRIENDS/PLAYED_WITH
 * below): friend rows (status, name), the heading count, country flags,
 * avatars. Action buttons (Invite/Message/Profile/Gift/Remove) render per
 * Figma but are inert until wired.
 *
 * Figma's decorative scrollbar graphics are omitted (app-wide
 * no-visible-scrollbars rule); long lists scroll invisibly instead.
 */

// ── Backend data slots ───────────────────────────────────────────────────────

interface FriendRowData {
  status:       'online' | 'in_game' | 'offline';
  name:         string;
  /** Raw country code from the payload — CountryFlag validates it. */
  countryCode?: string | null;
  /** Raw avatar key from the payload — PlayerAvatar validates it. */
  avatarKey?:   string | null;
  friendshipId?: number;
  onInvite?:    () => void;
  onRemove?:    () => void;
  onMessage?:   () => void;
  /** Present only when status is 'in_game' AND it's a real multiplayer match
   *  (see FriendListItem.spectate_game_id) — renders the Spectate action.
   *  Absent for solo/bot games, so no button shows for those. */
  onSpectate?:  () => void;
  unreadCount?: number;
}

const PLACEHOLDER_PLAYED_ROWS = 5;

const PLACEHOLDER_FRIEND_ROWS = 10;
const PLACEHOLDER_REQUEST_ROWS = 3;

// ── Pieces ──────────────────────────────────────────────────────────────────

/** Dim skeleton bar marking a backend-data slot. */
function Bar({ w }: { w: number }) {
  return (
    <span
      aria-hidden="true"
      style={{ display: 'inline-block', width: w, height: 14, borderRadius: 7, background: 'rgba(245,245,245,0.12)', verticalAlign: 'middle' }}
    />
  );
}

// Presence enum → colors here, label via catalog key at render (shared with
// Home's friend rows so both surfaces agree on the localized presence words).
const STATUS_META: Record<FriendRowData['status'], { color: string; bg: string; labelKey: 'friends.statusOnline' | 'friends.statusInGame' | 'friends.statusOffline' }> = {
  online:  { color: '#4CAF50', bg: 'rgba(76, 175, 80, 0.15)',  labelKey: 'friends.statusOnline'  },
  in_game: { color: '#A855F7', bg: 'rgba(168, 85, 247, 0.15)', labelKey: 'friends.statusInGame' },
  offline: { color: '#FF6B6B', bg: 'rgba(255, 107, 107, 0.15)', labelKey: 'friends.statusOffline' },
};

/** Status pill — Figma Overlay: tinted bg, 8px dot, colored label. */
function StatusBadge({ status }: { status?: FriendRowData['status'] }) {
  const { t } = useTranslation('home');
  if (!status) {
    return <span aria-hidden="true" style={{ width: 58, height: 22.3, borderRadius: 15, background: 'rgba(245,245,245,0.12)', flexShrink: 0 }} />;
  }
  const { color, bg, labelKey } = STATUS_META[status];
  const label = t(labelKey);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 52.5, height: 22.3, padding: '0 9px', borderRadius: 15, background: bg, flexShrink: 0 }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: color }} />
      <span style={{ fontFamily: DS2.font.body, fontWeight: 500, fontSize: 10.2, lineHeight: '16.5px', color }}>{label}</span>
    </span>
  );
}

const ACTION_GOLD: CSSProperties = {
  fontFamily: DS2.font.body,
  fontWeight: 600,
  fontSize:   14,
  lineHeight: '28px',
  color:      '#D4AF37',
  background: 'transparent',
  border:     'none',
  cursor:     'pointer',
  padding:    0,
  whiteSpace: 'nowrap',
  WebkitTapHighlightColor: 'transparent',
};

function FriendListRow({ row, compact }: { row?: FriendRowData; compact?: boolean }) {
  const { t: th } = useTranslation('home');
  const action: CSSProperties = compact ? { ...ACTION_GOLD, fontSize: 12.5, lineHeight: '26px' } : ACTION_GOLD;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 10 : 12.8, padding: compact ? '10px 10px' : '11.5px 12.8px', flexWrap: 'wrap' }}>
      <StatusBadge status={row?.status} />
      {/* Country flag slot (Figma 59×59) — 4:3 flag centered in the square;
          unset/invalid shows the neutral globe placeholder. */}
      <span style={{ width: compact ? 32 : 36.1, height: compact ? 32 : 36.1, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {row ? <CountryFlag countryCode={row.countryCode} width={compact ? 28 : 32} height={compact ? 21 : 24} /> : null}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8.6, minWidth: compact ? 100 : 120 }}>
        {/* Avatar (Figma 38.5px circle slot): authoritative preset or
            display-name initial — never a random/seeded avatar. */}
        {row
          ? <PlayerAvatar avatarKey={row.avatarKey} name={row.name} size={compact ? 22 : 25.6} />
          : <span aria-hidden="true" style={{ width: compact ? 22 : 25.6, height: compact ? 22 : 25.6, borderRadius: '50%', background: 'rgba(245,245,245,0.12)', flexShrink: 0 }} />}
        <span title={row ? row.name : undefined} style={{ fontFamily: DS2.font.body, fontWeight: 500, fontSize: compact ? 14 : 16.6, lineHeight: compact ? '26px' : '28.5px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>
          {row ? row.name : <Bar w={compact ? 100 : 120} />}
        </span>
      </span>
      {/* Actions — Figma: gold Invite/Message/Profile, gold 🎁 Gift, red ❌ Remove */}
      <span style={{ display: 'flex', alignItems: 'center', gap: compact ? 14 : 18.7, marginLeft: compact ? 0 : 'auto' }}>
        <span style={{ display: 'flex', gap: compact ? 12 : 14 }}>
          <button type="button" style={action} onClick={row?.onInvite} disabled={!row?.onInvite}>{th('friends.invite')}</button>
          {/* Only rendered when the friend is actually in a spectatable
              (real multiplayer) match — no button at all otherwise, not just
              a disabled one. See FriendRowData.onSpectate. */}
          {row?.onSpectate && (
            <button type="button" style={action} onClick={row.onSpectate}>👀 {th('friends.spectate')}</button>
          )}
          <button type="button" style={action} onClick={row?.onMessage} disabled={!row?.onMessage}>
            {th('friends.message')}
            {row && row.unreadCount ? <UnreadBadge count={row.unreadCount} /> : null}
          </button>
          <button type="button" style={{ ...action, opacity: 0.4, cursor: 'default' }} disabled>{th('friends.profile')}</button>
        </span>
        <span style={{ display: 'flex', gap: compact ? 10 : 10.7 }}>
          <button type="button" style={{ ...action, opacity: 0.4, cursor: 'default' }} disabled>🎁 {th('friends.gift')}</button>
          <button type="button" style={{ ...action, color: '#FF6B6B' }} onClick={row?.onRemove} disabled={!row?.onRemove}>❌ {th('friends.remove')}</button>
        </span>
      </span>
    </div>
  );
}

function RowList({ rows, placeholders, compact }: { rows: FriendRowData[]; placeholders: number; compact?: boolean }) {
  return (
    <div>
      {(rows.length ? rows : Array.from({ length: placeholders }, () => undefined)).map((row, i) => (
        <FriendListRow key={i} row={row} compact={compact} />
      ))}
    </div>
  );
}

interface PlayedWithRowData {
  name:             string;
  countryCode?:     string | null;
  avatarKey?:       string | null;
  isBot:            boolean;
  sessionsTogether: number;
  winRateTogether:  number | null;
  onInvite?:        () => void;
  onMessage?:       () => void;
  unreadCount?:     number;
}

function PlayedWithRow({ row, compact }: { row?: PlayedWithRowData; compact?: boolean }) {
  const { t } = useTranslation('friends');
  const { t: th } = useTranslation('home');
  const action: CSSProperties = compact ? { ...ACTION_GOLD, fontSize: 12.5, lineHeight: '26px' } : ACTION_GOLD;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 10 : 12.8, padding: compact ? '10px 10px' : '11.5px 12.8px', flexWrap: 'wrap' }}>
      <span style={{ width: compact ? 32 : 36.1, height: compact ? 32 : 36.1, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {row && !row.isBot ? <CountryFlag countryCode={row.countryCode} width={compact ? 28 : 32} height={compact ? 21 : 24} /> : null}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8.6, minWidth: compact ? 100 : 120 }}>
        {row
          ? <PlayerAvatar avatarKey={row.avatarKey} name={row.name} size={compact ? 22 : 25.6} />
          : <span aria-hidden="true" style={{ width: compact ? 22 : 25.6, height: compact ? 22 : 25.6, borderRadius: '50%', background: 'rgba(245,245,245,0.12)', flexShrink: 0 }} />}
        <span title={row ? row.name : undefined} style={{ fontFamily: DS2.font.body, fontWeight: 500, fontSize: compact ? 14 : 16.6, lineHeight: compact ? '26px' : '28.5px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>
          {row ? row.name : <Bar w={compact ? 100 : 120} />}
        </span>
      </span>
      <span style={{ fontFamily: DS2.font.body, fontWeight: 400, fontSize: compact ? 11.5 : 12.3, color: '#A89F86', minWidth: compact ? 140 : 153 }}>
        {/* Whole plural sentences (with/without win rate) — never joined fragments. */}
        {row
          ? (row.winRateTogether != null
              ? t('playedStatsWithRate', { count: row.sessionsTogether, rate: row.winRateTogether })
              : t('playedStats', { count: row.sessionsTogether }))
          : <Bar w={compact ? 120 : 150} />}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: compact ? 14 : 18.7, marginLeft: compact ? 0 : 'auto' }}>
        <span style={{ display: 'flex', gap: compact ? 12 : 14 }}>
          <button type="button" style={action} onClick={row?.onInvite} disabled={!row?.onInvite}>{th('friends.invite')}</button>
          <button type="button" style={action} onClick={row?.onMessage} disabled={!row?.onMessage}>
            {th('friends.message')}
            {row && row.unreadCount ? <UnreadBadge count={row.unreadCount} /> : null}
          </button>
          <button type="button" style={{ ...action, opacity: 0.4, cursor: 'default' }} disabled>{th('friends.profile')}</button>
        </span>
      </span>
    </div>
  );
}

/** Request row — same anatomy as a friend row, with Accept/Decline actions. */
function RequestRow({ row, onAccept, onDecline, compact }: { row?: { name: string; countryCode?: string | null; avatarKey?: string | null }; onAccept?: () => void; onDecline?: () => void; compact?: boolean }) {
  const { t } = useTranslation('friends');
  const action: CSSProperties = compact ? { ...ACTION_GOLD, fontSize: 12.5, lineHeight: '26px' } : ACTION_GOLD;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 10 : 12.8, padding: compact ? '10px 10px' : '11.5px 12.8px', flexWrap: 'wrap' }}>
      {/* Country flag slot — unset/invalid shows the neutral globe placeholder. */}
      <span style={{ width: compact ? 32 : 36.1, height: compact ? 32 : 36.1, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {row ? <CountryFlag countryCode={row.countryCode} width={compact ? 28 : 32} height={compact ? 21 : 24} /> : null}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8.6, minWidth: compact ? 100 : 120 }}>
        {row
          ? <PlayerAvatar avatarKey={row.avatarKey} name={row.name} size={compact ? 22 : 25.6} />
          : <span aria-hidden="true" style={{ width: compact ? 22 : 25.6, height: compact ? 22 : 25.6, borderRadius: '50%', background: 'rgba(245,245,245,0.12)', flexShrink: 0 }} />}
        <span title={row ? row.name : undefined} style={{ fontFamily: DS2.font.body, fontWeight: 500, fontSize: compact ? 14 : 16.6, lineHeight: compact ? '26px' : '28.5px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>
          {row ? row.name : <Bar w={compact ? 100 : 120} />}
        </span>
      </span>
      <span style={{ display: 'flex', gap: compact ? 12 : 14, marginLeft: compact ? 0 : 'auto' }}>
        <button type="button" style={action} onClick={onAccept} disabled={!onAccept}>{t('accept')}</button>
        <button type="button" style={{ ...action, color: '#FF6B6B' }} onClick={onDecline} disabled={!onDecline}>{t('decline')}</button>
      </span>
    </div>
  );
}

function InvitationRow({ name, countryCode, avatarKey, primaryLabel, onPrimary, secondaryLabel, onSecondary, compact }: {
  name: string;
  countryCode?: string | null;
  avatarKey?: string | null;
  primaryLabel?: string;
  onPrimary?: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  compact?: boolean;
}) {
  const action: CSSProperties = compact ? { ...ACTION_GOLD, fontSize: 12.5, lineHeight: '26px' } : ACTION_GOLD;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 10 : 12.8, padding: compact ? '10px 10px' : '11.5px 12.8px', flexWrap: 'wrap' }}>
      {/* Flag slot aligned with the friend/request rows; a real resolved
          player identity renders here, so the flag (or globe) applies. */}
      <span style={{ width: compact ? 32 : 36.1, height: compact ? 32 : 36.1, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {name ? <CountryFlag countryCode={countryCode} width={compact ? 28 : 32} height={compact ? 21 : 24} /> : null}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8.6, minWidth: compact ? 100 : 120 }}>
        {name
          ? <PlayerAvatar avatarKey={avatarKey} name={name} size={compact ? 22 : 25.6} />
          : <span aria-hidden="true" style={{ width: compact ? 22 : 25.6, height: compact ? 22 : 25.6, borderRadius: '50%', background: 'rgba(245,245,245,0.12)', flexShrink: 0 }} />}
        <span title={name || undefined} style={{ fontFamily: DS2.font.body, fontWeight: 500, fontSize: compact ? 14 : 16.6, lineHeight: compact ? '26px' : '28.5px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>
          {name}
        </span>
      </span>
      <span style={{ display: 'flex', gap: compact ? 12 : 14, marginLeft: compact ? 0 : 'auto' }}>
        {primaryLabel && (
          <button type="button" style={action} onClick={onPrimary} disabled={!onPrimary}>{primaryLabel}</button>
        )}
        {secondaryLabel && (
          <button type="button" style={{ ...action, color: '#FF6B6B' }} onClick={onSecondary} disabled={!onSecondary}>{secondaryLabel}</button>
        )}
      </span>
    </div>
  );
}

// Semantic friendship-status enum → catalog key, resolved at render.
const SEARCH_ACTION_KEY: Record<SearchFriendshipStatus, `searchAction.${SearchFriendshipStatus}`> = {
  none:              'searchAction.none',
  pending_sent:      'searchAction.pending_sent',
  pending_received:  'searchAction.pending_received',
  accepted:          'searchAction.accepted',
  blocked:           'searchAction.blocked',
};

function AddFriend() {
  const { t } = useTranslation('friends');
  const { sendFriendRequest, acceptFriendRequest } = useFriends();
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [overrides, setOverrides] = useState<Record<number, SearchFriendshipStatus>>({});

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }

    let stale = false;
    setSearching(true);
    const timer = setTimeout(() => {
      apiClient.searchUsers(q)
        .then(r => { if (!stale) setResults(r.users); })
        .catch(() => { if (!stale) setResults([]); })
        .finally(() => { if (!stale) setSearching(false); });
    }, 300);

    return () => { stale = true; clearTimeout(timer); };
  }, [query]);

  async function handleAction(user: UserSearchResult, status: SearchFriendshipStatus) {
    if (status === 'none') {
      await sendFriendRequest(user.email);
      setOverrides(prev => ({ ...prev, [user.id]: 'pending_sent' }));
    } else if (status === 'pending_received' && user.friendship_id != null) {
      await acceptFriendRequest(user.friendship_id);
      setOverrides(prev => ({ ...prev, [user.id]: 'accepted' }));
    }
  }

  const showDropdown = query.trim().length >= 2;

  return (
    <div style={{ position: 'relative', marginLeft: 'auto' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 9.7, background: '#FFFFFF', border: '1.5px solid #C9A84C', borderRadius: 20, padding: '9.3px 15.3px', width: 'min(287px, 50vw)', boxSizing: 'border-box' }}>
        <SearchIcon size={18} />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={t('searchPlaceholder')}
          style={{
            flex:       1,
            minWidth:   0,
            border:     'none',
            outline:    'none',
            background: 'transparent',
            color:      '#0B0B0B',
            fontFamily: DS2.font.body,
            fontWeight: 400,
            fontSize:   14,
          }}
        />
      </label>

      {showDropdown && (
        <div
          style={{
            position:     'absolute',
            top:          'calc(100% + 8px)',
            right:        0,
            width:        'min(360px, 90vw)',
            maxHeight:    320,
            overflowY:    'auto',
            background:   '#161616',
            border:       '1px solid rgba(201,168,76,0.3)',
            borderRadius: 12,
            boxShadow:    '0 12px 32px rgba(0,0,0,0.5)',
            zIndex:       20,
          }}
        >
          {searching ? (
            <div style={{ padding: '14px 18px', fontFamily: DS2.font.body, fontSize: 13, color: '#A89F86' }}>{t('searching')}</div>
          ) : results.length === 0 ? (
            <div style={{ padding: '14px 18px', fontFamily: DS2.font.body, fontSize: 13, color: '#A89F86' }}>{t('noPlayersFound')}</div>
          ) : (
            results.map(user => {
              const status = overrides[user.id] ?? user.friendship_status;
              const clickable = status === 'none' || status === 'pending_received';
              return (
                <div
                  key={user.id}
                  style={{
                    display:      'flex',
                    alignItems:   'center',
                    justifyContent: 'space-between',
                    gap:          12,
                    padding:      '10px 18px',
                    borderBottom: '1px solid rgba(245,245,245,0.08)',
                  }}
                >
                  <span style={{ fontFamily: DS2.font.body, fontSize: 14, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.display_name}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleAction(user, status)}
                    disabled={!clickable}
                    style={{
                      flexShrink: 0,
                      background: 'transparent',
                      border:     'none',
                      cursor:     clickable ? 'pointer' : 'default',
                      fontFamily: DS2.font.body,
                      fontWeight: 600,
                      fontSize:   13,
                      color:      clickable ? '#D4AF37' : '#777777',
                      padding:    0,
                    }}
                  >
                    {t(SEARCH_ACTION_KEY[status])}
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/** Black magnifying-glass icon — same mark used in Play Online's search field. */
function SearchIcon({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="#0B0B0B" strokeWidth="2" />
      <path d="M20 20l-3.2-3.2" stroke="#0B0B0B" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// ── Screen ──────────────────────────────────────────────────────────────────

export function FriendsDS2Screen() {
  const isMobile = useIsMobile();
  const { t } = useTranslation('friends');
  const { t: th } = useTranslation('home');
  const [tab, setTab] = useState<'friends' | 'requests' | 'invitations'>('friends');
  const {
    friends, pendingRequests, friendsLoading, requestsLoading,
    incomingInvitations, outgoingInvitations, invitationsLoading,
    removeFriend, acceptFriendRequest, declineFriendRequest,
    acceptInvitation, declineInvitation, cancelInvitation,
    conversations,
  } = useFriends();
  const navigate = useNavigate();
  const [partners, setPartners] = useState<PartnerStats[]>([]);
  const [partnersLoaded, setPartnersLoaded] = useState(false);

  useEffect(() => {
    let stale = false;
    apiClient.getPartnerStats()
      .then(r => { if (!stale) setPartners(r.partners); })
      .catch(() => { if (!stale) setPartners([]); })
      .finally(() => { if (!stale) setPartnersLoaded(true); });
    return () => { stale = true; };
  }, []);

  async function handleInvite(friendUserId: number) {
    try {
      const sessions = await apiClient.getSessions();
      const sessionId = sessions.my_active_session_id ?? (await apiClient.createSession()).session_id;
      await apiClient.sendInvitation(friendUserId, sessionId);
      navigate(`/lobby/${sessionId}`);
    } catch (e) {
      // Phase 12 (C3a): coded errors localize via the catalog; uncoded
      // backend prose stays verbatim; the generic case is ours. Toasts
      // resolve at fire time (the established toast pattern).
      showSocialToast(renderUiMessage(th, uiFromError(e, 'friends.inviteError')));
    }
  }

  async function handleAcceptInvitation(invitationId: number) {
    try {
      const invitation = await acceptInvitation(invitationId);
      await apiClient.joinSession(invitation.session.id);
      navigate(`/lobby/${invitation.session.id}`);
    } catch (e) {
      showSocialToast(renderUiMessage(t, uiFromError(e, 'toast.acceptInvitationError')));
    }
  }

  const friendRows: FriendRowData[] = friends.map(f => ({
    status:       f.status,
    name:         f.friend.display_name,
    countryCode:  f.friend.country_code ?? null,
    avatarKey:    f.friend.avatar_key ?? null,
    friendshipId: f.friendship_id,
    onInvite:     () => handleInvite(f.friend.id),
    onRemove:     () => removeFriend(f.friendship_id),
    onMessage:    () => navigate(`/messages/${f.friend.id}`),
    onSpectate:   f.spectate_game_id != null ? () => navigate(`/spectate/${f.spectate_game_id}`) : undefined,
    unreadCount:  conversations.find(c => c.friend.id === f.friend.id)?.unread_count ?? 0,
  }));

  const playedWithRows: PlayedWithRowData[] = partners.map(p => {
    const friendMatch = friends.find(f => f.friend.id === p.partner_user_id);
    return {
      name:             p.partner_display_name,
      countryCode:      p.partner_country_code,
      avatarKey:        p.partner_avatar_key ?? null,
      isBot:            p.is_bot,
      sessionsTogether: p.sessions_together,
      winRateTogether:  p.win_rate_together,
      onInvite:         friendMatch ? () => handleInvite(friendMatch.friend.id) : undefined,
      onMessage:        friendMatch
        ? () => navigate(`/messages/${friendMatch.friend.id}`)
        : undefined,
      unreadCount: friendMatch ? conversations.find(c => c.friend.id === friendMatch.friend.id)?.unread_count ?? 0 : 0,
    };
  });

  return (
    // flex column fills the available viewport height so Footer's
    // marginTop:'auto' sticks flush to the bottom on short pages (see
    // AppShell.tsx's content-wrapper comment).
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

      {/* ── FRIENDS LIST ────────────────────────────────────────────── */}
      <div style={{ position: 'relative', paddingTop: isMobile ? 28 : 53, paddingBottom: isMobile ? 28 : 53 }}>
        <AtmosphericGlow width={825} height={639} top={130} leftOffset={-109} />
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          {/* Friends (12) — Figma: white Montserrat 92 + gray Roboto 25 count.
              Add-friend button on the heading row's right. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 14 : 16.6, flexWrap: 'wrap' }}>
            <h1
              style={{
                fontSize:      isMobile ? 22 : `clamp(1.6rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
                lineHeight:    isMobile ? '26px' : DS2.type.sectionHeading.lineHeight,
                letterSpacing: '-0.02em',
                fontWeight:    700,
                fontFamily:    DS2.font.heading,
                color:         '#FFFFFF',
                margin:        0,
                display:       'flex',
                alignItems:    'center',
                gap:           isMobile ? 12 : 16.6,
              }}
            >
              {t('title')}
              {!friendsLoading && (
                <span style={{ fontFamily: DS2.font.body, fontWeight: 400, fontSize: isMobile ? 14 : 15.6, lineHeight: isMobile ? '17px' : '19px', color: '#999999' }}>
                  {t('count', { count: friendRows.length })}
                </span>
              )}
            </h1>
            <AddFriend />
          </div>
          {!isMobile && (
            <p
              style={{
                fontSize:   `clamp(0.93rem, ${DS2.type.paragraph.pref}vw, ${DS2.type.paragraph.max}px)`,
                lineHeight: DS2.type.paragraph.lineHeight,
                fontFamily: DS2.font.body,
                fontWeight: 300,
                color:      DS2.color.textBright,
                margin:     '15px 0 0',
              }}
            >
              {th('friends.sub')}
            </p>
          )}

          {/* Tabs — shared DS2 FilterPill (same as Home's match filter) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16.3, marginTop: isMobile ? 16 : 25.3 }}>
            <FilterPill label={t('tabFriends')} active={tab === 'friends'} onClick={() => setTab('friends')} compact={isMobile} />
            <FilterPill label={t('tabRequests')} active={tab === 'requests'} onClick={() => setTab('requests')} badge={pendingRequests.length} compact={isMobile} />
            <FilterPill label={t('tabInvitations')} active={tab === 'invitations'} onClick={() => setTab('invitations')} badge={incomingInvitations.length} compact={isMobile} />
          </div>

          <div style={{ marginTop: isMobile ? 16 : 21.3, position: 'relative' }}>
            {tab === 'friends' ? (
              friendsLoading ? (
                <RowList rows={[]} placeholders={PLACEHOLDER_FRIEND_ROWS} compact={isMobile} />
              ) : friendRows.length ? (
                <RowList rows={friendRows} placeholders={PLACEHOLDER_FRIEND_ROWS} compact={isMobile} />
              ) : (
                <div style={{ padding: '14.4px', fontFamily: DS2.font.body, fontSize: 14, color: '#A89F86' }}>
                  {t('emptyFriends')}
                </div>
              )
            ) : tab === 'requests' ? (
              requestsLoading ? (
                Array.from({ length: PLACEHOLDER_REQUEST_ROWS }, (_, i) => <RequestRow key={i} compact={isMobile} />)
              ) : pendingRequests.length ? (
                pendingRequests.map(req => (
                  <RequestRow
                    key={req.friendship_id}
                    row={{ name: req.from.display_name, countryCode: req.from.country_code ?? null, avatarKey: req.from.avatar_key ?? null }}
                    onAccept={() => acceptFriendRequest(req.friendship_id)}
                    onDecline={() => declineFriendRequest(req.friendship_id)}
                    compact={isMobile}
                  />
                ))
              ) : (
                <div style={{ padding: '14.4px', fontFamily: DS2.font.body, fontSize: 14, color: '#A89F86' }}>
                  {t('emptyRequests')}
                </div>
              )
            ) : invitationsLoading ? (
              Array.from({ length: PLACEHOLDER_REQUEST_ROWS }, (_, i) => <InvitationRow key={i} name="" compact={isMobile} />)
            ) : incomingInvitations.length || outgoingInvitations.length ? (
              <>
                {incomingInvitations.map(inv => (
                  <InvitationRow
                    key={inv.id}
                    name={inv.from_user.display_name}
                    countryCode={inv.from_user.country_code ?? null}
                    avatarKey={inv.from_user.avatar_key ?? null}
                    primaryLabel={t('accept')}
                    onPrimary={() => handleAcceptInvitation(inv.id)}
                    secondaryLabel={t('decline')}
                    onSecondary={() => declineInvitation(inv.id)}
                    compact={isMobile}
                  />
                ))}
                {outgoingInvitations.map(inv => (
                  <InvitationRow
                    key={inv.id}
                    name={inv.to_user.display_name}
                    countryCode={inv.to_user.country_code ?? null}
                    avatarKey={inv.to_user.avatar_key ?? null}
                    secondaryLabel={t('common:action.cancel' as never)}
                    onSecondary={() => cancelInvitation(inv.id)}
                    compact={isMobile}
                  />
                ))}
              </>
            ) : (
              <div style={{ padding: '14.4px', fontFamily: DS2.font.body, fontSize: 14, color: '#A89F86' }}>
                {t('emptyInvitations')}
              </div>
            )}
          </div>
        </PageContent>
      </div>

      {/* ── PLAYERS YOU'VE PLAYED WITH — Figma Rectangle 14: #131313 band ── */}
      <div style={{ position: 'relative', background: '#131313', paddingTop: isMobile ? 28 : 53, paddingBottom: isMobile ? 28 : 53 }}>
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          {/* Figma: Montserrat 700 60 white */}
          <h2
            style={{
              fontFamily:    DS2.font.heading,
              fontWeight:    700,
              fontSize:      isMobile ? 20 : `clamp(1.45rem, 1.68vw, 36.3px)`,
              lineHeight:    isMobile ? '24px' : 1.1,
              letterSpacing: '-0.02em',
              color:         '#FFFFFF',
              margin:        0,
            }}
          >
            {t('playedWithTitle')}
          </h2>

          <div style={{ marginTop: isMobile ? 20 : 29.3 }}>
            {!partnersLoaded ? (
              Array.from({ length: PLACEHOLDER_PLAYED_ROWS }, (_, i) => <PlayedWithRow key={i} compact={isMobile} />)
            ) : playedWithRows.length ? (
              playedWithRows.map((row, i) => <PlayedWithRow key={i} row={row} compact={isMobile} />)
            ) : (
              <div style={{ padding: '14.4px', fontFamily: DS2.font.body, fontSize: 14, color: '#A89F86' }}>
                {t('emptyPlayedWith')}
              </div>
            )}
          </div>
        </PageContent>
      </div>

      <Footer />
    </div>
  );
}
